import { Button, Icon, Popover } from "@screencloud/screencloud-ui-components";
import { MouseEvent, useState } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { CompareContentItem } from "../../types";
import {
  CHANGE_ROW_TINT,
  PLAIN_ROW_HOVER_BACKGROUND,
} from "../../utils/changeStatus";
import { ChangeStatusLabel } from "../ChangeStatusLabel/ChangeStatusLabel";
import {
  AccentBar,
  Duration,
  HighlightOverlay,
  Position,
  Row,
  RowActions,
  StatusSlot,
  Subtitle,
  Thumbnail,
  Title,
  TitleBlock,
} from "./styles";

export interface CompareRowProps {
  item: CompareContentItem;
  /** Column identity, keeps test ids unique when an item appears on both sides. */
  sideKey: string;
  /** Locates this item in the changes column (live rows only, GRWBKK-1767). */
  onFind?: () => void;
  /** Temporarily marks this row as the find target. */
  highlighted?: boolean;
  /** Reports pointer hover so the matching row can mirror it (live rows). */
  onHoverChange?: (hovering: boolean) => void;
  /** Mirrors the hover of the corresponding live row (changes rows). */
  linkedHover?: boolean;
  /** Previews the item in the modal (action button only). */
  onPreview?: () => void;
  /** Opens the item in a new tab (action button only). */
  onOpen?: () => void;
}

interface RowAction {
  key: string;
  icon: string;
  label: string;
  onClick: () => void;
}

const CompareRowBase = ({
  item,
  sideKey,
  onFind,
  highlighted,
  onHoverChange,
  linkedHover,
  onPreview,
  onOpen,
  intl,
}: CompareRowProps & InjectedIntlProps) => {
  // Controlled so a tooltip closes on click: an action can cover its own
  // button (e.g. the preview pane), and then no mouseleave ever arrives.
  const [tooltipKey, setTooltipKey] = useState<string>();
  const { changeType, movedFrom, movedTo } = item;
  const tint = changeType ? CHANGE_ROW_TINT[changeType] : null;
  const isRemoved = changeType === "removed";
  const rowTestId = `compare-${sideKey}-row-${item.id}`;
  const isEditedAndMoved =
    changeType === "edited" && movedFrom !== undefined && movedTo !== undefined;

  const statusLabel = changeType && (
    <ChangeStatusLabel changeType={changeType} idSuffix={item.id} />
  );

  const actions: RowAction[] = [
    onPreview && {
      key: "preview",
      icon: "preview",
      label: intl.formatMessage({
        id: "compare_changes.action.preview",
        defaultMessage: "Preview",
      }),
      onClick: onPreview,
    },
    onOpen && {
      key: "open",
      icon: "external",
      label: intl.formatMessage({
        id: "compare_changes.action.open_in_new_tab",
        defaultMessage: "Open in new tab",
      }),
      onClick: onOpen,
    },
    onFind && {
      key: "find",
      icon: "search",
      label: intl.formatMessage({
        id: "compare_changes.find",
        defaultMessage: "Find",
      }),
      onClick: onFind,
    },
  ].filter((action): action is RowAction => Boolean(action));

  const actionBar = actions.length > 0 && (
    <RowActions>
      {actions.map((action) => (
        <Popover
          key={action.key}
          inverted
          on="hover"
          position="top center"
          content={action.label}
          open={tooltipKey === action.key}
          onOpen={() => setTooltipKey(action.key)}
          onClose={() => setTooltipKey(undefined)}
          trigger={
            <Button
              icon
              borderless
              mini
              data-testid={`${rowTestId}-${action.key}`}
              aria-label={action.label}
              onClick={(event: MouseEvent) => {
                // The row click runs Find; don't fire it as well.
                event.stopPropagation();
                setTooltipKey(undefined);
                action.onClick();
              }}
            >
              <Icon name={action.icon} />
            </Button>
          }
        />
      ))}
    </RowActions>
  );

  return (
    <Row
      data-testid={rowTestId}
      data-linked-hover={linkedHover || undefined}
      $background={tint?.background}
      $hoverBackground={tint?.hoverBackground ?? PLAIN_ROW_HOVER_BACKGROUND}
      $linkedHover={linkedHover}
      $clickable={Boolean(onFind)}
      onClick={onFind}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
    >
      {tint && <AccentBar $color={tint.bar} />}
      {highlighted && (
        <HighlightOverlay data-testid={`${rowTestId}-highlight`} />
      )}
      <Position
        $highlighted={Boolean(changeType) && changeType !== "unchanged"}
      >
        {item.position}.
      </Position>
      <Thumbnail $isAppIcon={item.isAppIcon}>
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}
      </Thumbnail>
      <TitleBlock>
        <Title data-testid={`${rowTestId}-title`} $muted={isRemoved}>
          {item.title}
        </Title>
        {item.subtitle && <Subtitle>{item.subtitle}</Subtitle>}
      </TitleBlock>
      {item.durationLabel && <Duration>{item.durationLabel}</Duration>}
      {actionBar}
      <StatusSlot $yieldsToActions={actions.length > 0}>
        {isEditedAndMoved ? (
          <Popover
            inverted
            content={
              <FormattedMessage
                id="compare_changes.tooltip.edited_and_moved"
                defaultMessage="Edited · Moved from position {from} to {to}"
                values={{ from: movedFrom, to: movedTo }}
              />
            }
            trigger={
              <span data-testid={`${rowTestId}-status`}>{statusLabel}</span>
            }
          />
        ) : (
          statusLabel
        )}
      </StatusSlot>
    </Row>
  );
};

export const CompareRow = injectIntl(CompareRowBase);
